// src/components/portal/KPIStatCard.tsx
import React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent } from "../ui/card";

export type TrendDirection = "up" | "down";

type Props = {
  /** left icon node, e.g. <Accessibility className="w-6 h-6 text-orange-600" /> */
  icon: React.ReactNode;
  /** small label above the KPI value (e.g., "Motoria") */
  label: string;
  /** KPI value (e.g., "82%" or 82) */
  value: string | number;
  /** optional delta text (e.g., "+5.2%") */
  delta?: string;
  /** direction of the delta, adjusts icon and default color */
  trendDirection?: TrendDirection;
  /** tiny caption under the value (e.g., "Average across 36 facilities") */
  caption?: string;
  /** override classes for the big value (defaults to category color) */
  valueClassName?: string;
  /** override classes for the delta pill (defaults by trendDirection) */
  deltaClassName?: string;
  /** background class for the circular icon container */
  iconBgClassName?: string;
  /** pass extra classes to the root Card */
  className?: string;
};

export default function KPIStatCard({
  icon,
  label,
  value,
  delta,
  trendDirection = "up",
  caption,
  valueClassName,
  deltaClassName,
  iconBgClassName,
  className,
}: Props) {
  const isDown = trendDirection === "down";
  const DeltaIcon = isDown ? TrendingDown : TrendingUp;

  const computedDeltaClass = deltaClassName || (isDown ? "text-red-600" : "text-green-600");
  const computedValueClass = valueClassName || "text-orange-600";
  const computedIconBg = iconBgClassName || "bg-blue-100";

  return (
    <Card className={`relative overflow-hidden ${className || ""}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`w-12 h-12 rounded-full ${computedIconBg} flex items-center justify-center`}>
            {icon}
          </div>
          {!!delta && (
            <div className={`flex items-center gap-1 text-ss ${computedDeltaClass}`}>
              <DeltaIcon className="w-3 h-3" />
              {delta}
            </div>
          )}
        </div>
        <div>
          <p className="text-s text-muted-foreground mb-1">{label}</p>
          <p className={`text-xxl font-medium ${computedValueClass}`}>{typeof value === "number" ? `${value}` : value}</p>
          <p className="text-ss text-muted-foreground mt-1">{caption || "Media tra le strutture"}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/*
Usage example (matches the requested UI standard):

<KPIStatCard
  icon={<Accessibility className="w-6 h-6 text-blue-600" />}
  label="Motoria"
  value="82%"
  delta="+5.2%"
  trendDirection="up"
  caption="Average across 36 facilities"
/>

You can change colors per category via props:
- valueClassName (e.g., "text-purple-600")
- iconBgClassName (e.g., "bg-purple-100")
- trendDirection / deltaClassName
*/
